import { Routes, Route, Navigate } from "react-router-dom";
import * as React from "react";

import FlowCanvasPage from "@features/flows/pages/FlowCanvasPage";
import FlowFilesPage from "@features/flows/flowfs/pages/FlowFilesPage";
import FlowsListPage from "@features/flows/pages/FlowsListPage";
import TestSuitePage from "@features/test-suite/pages/TestSuitePage";
import env from "@shared/api/env";
import { RequireAuth } from "@app/auth/RequireAuth";
import RequireWorkspace from "@app/auth/RequireWorkspace";
import UsersPage from "@app/admin/UsersPage";
import { LoginPage } from "@app/auth/LoginPage";
import { ResetPasswordRequestPage } from "@app/auth/ResetPasswordRequestPage";
import { UpdatePasswordPage } from "@app/auth/UpdatePasswordPage";
import { AppLayout } from "@app/layout/AppLayout";
import { AuthLayout } from "@app/auth/AuthLayout";
import WorkspaceSetupPage from "@features/workspaces/pages/WorkspaceSetupPage";
import ChatPage from "@features/chat/pages/ChatPage";


export function AppRouter() {
  const DEV = env.NODE_ENV !== "production";
  // Lazy-load dev pages to keep them out of prod bundles
  const DevRoutes = DEV
    ? (() => {
        const DevtoolsPage = React.lazy(() => import("@features/devtools/pages/DevtoolsPage"));
        const APITesterPage = React.lazy(() => import("@features/devtools/pages/APITesterPage"));
        return { DevtoolsPage, APITesterPage };
      })()
    : null;
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/flows" replace />} />

      {/* Auth */}
      <Route path="/auth/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/auth/reset" element={<AuthLayout><ResetPasswordRequestPage /></AuthLayout>} />
      <Route path="/auth/update-password" element={<AuthLayout><UpdatePasswordPage /></AuthLayout>} />

      {/* Workspace setup */}
      <Route
        path="/workspace/new"
        element={
          <RequireAuth>
            <AuthLayout>
              <WorkspaceSetupPage />
            </AuthLayout>
          </RequireAuth>
        }
      />

      {/* Flows */}
      <Route
        path="/flows"
        element={
          <RequireAuth>
            <RequireWorkspace>
              <AppLayout>
                <FlowsListPage />
              </AppLayout>
            </RequireWorkspace>
          </RequireAuth>
        }
      />
      <Route
        path="/flows/:flowId"
        element={
          <RequireAuth>
            <RequireWorkspace>
              <AppLayout>
                {/* Clean, production-safe page (no dev chrome) */}
                <FlowCanvasPage />
              </AppLayout>
            </RequireWorkspace>
          </RequireAuth>
        }
      />
      <Route
        path="/flows/:flowId/files"
        element={
          <RequireAuth>
            <RequireWorkspace>
              <AppLayout>
                <FlowFilesPage />
              </AppLayout>
            </RequireWorkspace>
          </RequireAuth>
        }
      />
      <Route
        path="/flows/tests"
        element={
          <RequireAuth>
            <RequireWorkspace>
              <AppLayout><TestSuitePage /></AppLayout>
            </RequireWorkspace>
          </RequireAuth>
        }
      />

      <Route
        path="/chat"
        element={
          <RequireAuth>
            <RequireWorkspace>
              <AppLayout><ChatPage /></AppLayout>
            </RequireWorkspace>
          </RequireAuth>
        }
      />

      {/* Dev tools (not available in production) */}
{DEV && DevRoutes && (
        <>
          <Route path="/dev" element={<RequireAuth><RequireWorkspace><AppLayout><React.Suspense fallback={<div className="p-4 text-sm">Loading dev tools…</div>}><DevRoutes.DevtoolsPage /></React.Suspense></AppLayout></RequireWorkspace></RequireAuth>} />
          <Route path="/dev/:storyId" element={<RequireAuth><RequireWorkspace><AppLayout><React.Suspense fallback={<div className="p-4 text-sm">Loading dev tools…</div>}><DevRoutes.DevtoolsPage /></React.Suspense></AppLayout></RequireWorkspace></RequireAuth>} />
          <Route path="/dev/api" element={<RequireAuth><RequireWorkspace><AppLayout><React.Suspense fallback={<div className="p-4 text-sm">Loading API tester…</div>}><DevRoutes.APITesterPage /></React.Suspense></AppLayout></RequireWorkspace></RequireAuth>} />
        </>
      )}

      {/* Admin */}
      <Route path="/admin/users" element={<RequireAuth><RequireWorkspace><AppLayout><UsersPage /></AppLayout></RequireWorkspace></RequireAuth>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/flows" replace />} />
    </Routes>
  );
}

export default AppRouter;
